/**
 * Workflow Update Sidebar
 *
 * Smart update workflow with new ComfyUI JSON while preserving UI edits
 * (labels, descriptions, field order, groups, etc.)
 */

import React, { useState } from 'react';


interface MergeReport {
  added: string[];
  removed: string[];
  updated: string[];
  preserved: string[];
}

interface WorkflowUpdateSidebarProps {
  workflowId: string;
  workflowName: string;
  tenantId: string;
  apiBaseUrl: string;
  authToken: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const WorkflowUpdateSidebar: React.FC<WorkflowUpdateSidebarProps> = ({
  workflowId,
  workflowName,
  tenantId,
  apiBaseUrl,
  authToken,
  onClose,
  onSuccess
}) => {
  const [comfyWorkflowFile, setComfyWorkflowFile] = useState<File | null>(null);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mergeReport, setMergeReport] = useState<MergeReport | null>(null);
  const [newVersion, setNewVersion] = useState<string | null>(null);

  const handleFileChange = (file: File | null) => {
    setComfyWorkflowFile(file);
    setError(null);
    setMergeReport(null);
  };

  const handleUpdate = async () => {
    setError(null);
    setMergeReport(null);

    if (!comfyWorkflowFile) {
      setError('Please select a workflow file');
      return;
    }

    setUpdating(true);

    try {
      const formData = new FormData();
      formData.append('comfyWorkflow', comfyWorkflowFile);

      const response = await fetch(
        `${apiBaseUrl}/api/workflows/${workflowId}/update?tenantId=${encodeURIComponent(tenantId)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` },
          body: formData
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || data.error || `Update failed: ${response.statusText}`);
      }

      const result = await response.json();
      setMergeReport(result.mergeReport);
      setNewVersion(result.newVersion);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDone = () => {
    onSuccess();
  };

  const hasChanges = mergeReport && (
    mergeReport.added.length > 0 ||
    mergeReport.removed.length > 0 ||
    mergeReport.updated.length > 0
  );

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={mergeReport ? undefined : onClose} aria-hidden="true" />
      <div className="w-96 flex-shrink-0 bg-white shadow-xl flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Update Workflow</h2>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-fast text-lg">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div className="text-label text-gray-700 font-medium bg-surface-subtle border border-gray-200 rounded-lg px-4 py-3">
            <strong>Updating:</strong> {workflowName}
          </div>

          {!mergeReport && (
            <>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="update-file-input" className="text-label font-medium text-gray-700">
                  Select Updated ComfyUI Workflow (JSON)
                </label>
                <input id="update-file-input" type="file" accept=".json"
                  onChange={e => handleFileChange(e.target.files?.[0] || null)}
                  disabled={updating}
                  className="text-label text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-label file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer disabled:opacity-50" />
                {comfyWorkflowFile && <span className="text-meta text-success-dark">✓ {comfyWorkflowFile.name}</span>}
              </div>

              <div className="bg-info-light border border-info/30 rounded-lg p-4">
                <h4 className="text-label font-semibold text-info-dark mb-2">✨ Smart Update</h4>
                <p className="text-label text-info-dark mb-2">Your customizations will be preserved:</p>
                <ul className="flex flex-col gap-1">
                  {['Custom labels & descriptions', 'Help text & tooltips', 'Field order & grouping', 'Widget settings'].map(f => (
                    <li key={f} className="text-label text-info-dark flex items-center gap-1.5"><span className="text-success">✓</span>{f}</li>
                  ))}
                </ul>
                <p className="text-label text-info-dark mt-2">Only the underlying workflow graph will be updated.</p>
              </div>
            </>
          )}

          {mergeReport && (
            <div className="flex flex-col gap-4">
              <div className="bg-success-light border border-success/30 rounded-lg p-4">
                <h4 className="text-body font-semibold text-success-dark mb-1">✅ Update Complete</h4>
                <p className="text-label text-success-dark">Version updated to <strong>{newVersion}</strong></p>
              </div>
              {[
                { items: mergeReport.added,     prefix: '➕', label: 'New Parameters',    cls: 'bg-success-light border-success/30 text-success-dark' },
                { items: mergeReport.removed,   prefix: '➖', label: 'Removed Parameters', cls: 'bg-error-light border-error/30 text-error-dark'     },
                { items: mergeReport.updated,   prefix: '🔄', label: 'Updated Mappings',   cls: 'bg-info-light border-info/30 text-info-dark'         },
                { items: mergeReport.preserved, prefix: '✓',  label: 'Preserved',          cls: 'bg-gray-100 border-gray-200 text-gray-600'           },
              ].filter(g => g.items.length > 0).map(({ items, prefix, label, cls }) => (
                <div key={label} className={`border rounded-lg p-4 ${cls}`}>
                  <h5 className="text-label font-semibold mb-2">{prefix} {label} ({items.length})</h5>
                  <ul className="list-disc list-inside flex flex-col gap-0.5">
                    {items.map(param => <li key={param} className="text-label">{param}</li>)}
                  </ul>
                </div>
              ))}
              {!hasChanges && (
                <p className="text-label text-gray-500 text-center py-2">No parameter changes detected. Workflow graph updated.</p>
              )}
            </div>
          )}

          {error && (
            <div className="px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          {!mergeReport ? (
            <>
              <button type="button" onClick={handleUpdate} disabled={updating || !comfyWorkflowFile}
                className="flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast disabled:opacity-50">
                {updating ? 'Updating…' : 'Update Workflow'}
              </button>
              <button type="button" onClick={onClose} disabled={updating}
                className="px-4 py-2.5 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast disabled:opacity-50">
                Cancel
              </button>
            </>
          ) : (
            <button type="button" onClick={handleDone}
              className="flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

